<template>
    <Attr :widget_id="widget_id" :attr_id="attr_id">
        <div class="attr-css"
            v-wjTooltip="attr.tooltip"
            v-wjContextMenu="'ContextWidgetMenuId'"
        >
            <label class="label-attr" v-if="attr.title && !attr.hideTitle" :for="attr.html_id">{{ attr.title }}</label>

            <div class="grid-top-line css-top-line" ref="attr_top_line" v-if="!attr?.hideCommandPanel">
                <div class="commandPanel-area" ref="commandPanelArea"
                    :style="commandPanelAreaWidth != null ? { flex: '0 1 ' + commandPanelAreaWidth + 'px' } : (!commandPanel.length && !commandPanelOverflow.length ? { flex: '0 0 0', minWidth: '0', overflow: 'hidden' } : {})"
                >
                    <div class="commandPanel-menu" v-if="commandPanel.length && commandPanelMaxVisible !== 0" ref="commandPanelMenu"
                        :style="commandPanelMenuWidth != null ? { flex: '0 0 ' + commandPanelMenuWidth + 'px' } : {}"
                    >
                        <span v-for="(attrM, index) in commandPanel"
                            :key="attrM.html_id"
                            class="cp-item-wrap"
                            v-show="index < commandPanelMaxVisible"
                        >
                            <component
                                :widget_id="widget_id"
                                :is="attrM.component"
                                :attr_id="attrM.id"
                                :initialAttr="attrM"
                            />
                        </span>
                    </div>
                    <div class="widget_menu commandPanel-overflow-btn" v-if="commandPanelOverflow.length">
                        <wj-menu
                            class="commandPanel-overflow-menu"
                            :header="''"
                            :displayMemberPath="'titleAction'"
                            :subItemsPath="'attrs'"
                            :showDropDownButton="false"
                            :openOnHover="true"
                            :isAnimated="false"
                            :itemsSource="commandPanelOverflow"
                            :itemClicked="menuNodeClicked"
                            :formatItem="store.formatMenuItem"
                        >
                            <i class="pi pi-ellipsis-v"></i>
                        </wj-menu>
                    </div>
                </div>

                <!-- IE 2026 09 23 / ODOO-341407: i18n -->
                <div v-if="isError" class="css-error">
                    ⚠️ {{ t('Error converting text to CSS') }}
                </div>

                <!-- IE 2026 09 28 / ODOO-342342 (заказ Игоря: «"меню" — прижато вправо и форма меню как у ≡ таблицы»): ≡ поля — панель функций MenuPanel (те же пункты actionMenu:
                     Command panel — Edit CSS / Apply / Cancel; поиск, избранное) у правого края строки (чипсов у поля нет — раньше ≡ стоял сразу за ✎);
                     прежний wj-menu — флаг fe.ux.menuPanelEverywhere выключен / мобильный профиль (utils/menuPanelHost.js) -->
                <div class="widget_menu mr-2" :class="{ 'menu-push-right': menuPanelHost_on() }">
                    <MenuPanel v-if="actionMenu.length && !attr?.dropdown && menuPanelHost_on()" :items="actionMenu" align="right" :caret="false" btnClass="widget-menu-btn field-menu-btn"
                        :title="t('Field menu')" titleIcon="pi pi-bars" :subtitle="menuPanelSubtitle()" :openDelay="200" :runsTitle="t('Field')" search :searchPlaceholder="t('Search commands…')"
                        :favKey="menuFavKind()" :favOf="menuFavOf" :epoch="store._favEpoch" :favTitle="t('Favourite command')" help="field" :btnShort="t('Field menu')"
                        @pick="menuPanelPick" @fav="menuFav">
                        <template #trigger><i class="pi pi-bars pi-bold"></i></template>
                    </MenuPanel>
                    <wj-menu id="actionMenuId" :displayMemberPath="'titleAction'" :subItemsPath="'attrs'" :showDropDownButton="false"
                        v-else-if="actionMenu.length && !attr?.dropdown"
                        :openOnHover="true" :isAnimated="false" :itemsSource="actionMenu" :itemClicked="menuNodeClicked" :formatItem="store.formatMenuItem"
                    >
                        <i class="pi pi-bars pi-bold"></i>
                    </wj-menu>
                </div>
            </div>

            <Codemirror
                :id="attr.html_id"
                v-model:value="content_str"
                v-visible="handleVisibility"
                :options="cmOptions"
                @change="attr_set"
                @ready="editorReady"
            />
        </div>
    </Attr>
</template>

<script>
import { useMainStore } from '@/stores/mainStore'

import Codemirror from "codemirror-editor-vue3";
import "codemirror/mode/javascript/javascript.js";
import "codemirror/mode/sql/sql.js";
import "codemirror/theme/dracula.css";
import "codemirror/addon/fold/foldcode";
import "codemirror/addon/fold/foldgutter";
import "codemirror/addon/fold/brace-fold";
import "codemirror/addon/fold/foldgutter.css";
import "codemirror/theme/neo.css";

import "codemirror/mode/css/css.js";

import "codemirror/addon/hint/show-hint.js";
import "codemirror/addon/hint/show-hint.css";
import "codemirror/addon/hint/css-hint.js";

import "codemirror/addon/hint/anyword-hint.js";
import { menuPanelHostMixin } from '@/utils/menuPanelHost'   // IE 2026 09 28 / ODOO-342342: ≡ поля — MenuPanel (как ≡ таблицы), прижат вправо

export default {
    mixins: [menuPanelHostMixin],   // IE 2026 09 28 / ODOO-342342
    props: {
        widget_id: [String, Number],
        attr_id: String,
        initialAttr: {
            type: Object,
            default: null,
        },
        modelValue: { type: [String, Object], default: "" },
    },
    emits: ['update:modelValue'],
    data: () => ({
        WIDGET: {},
        content_str: '',
        isError: false,
        editMode: false,
        ind_commandPanel: 0,
        cmOptions: {
            mode: 'css',
            theme: "neo",
            lineNumbers: true,
            smartIndent: true,
            indentUnit: 4,
            foldGutter: true,
            gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
            styleActiveLine: true,
            extraKeys: { "Ctrl-Space": "autocomplete" },
            hintOptions: {
                completeSingle: false,
            },
        },

        commandPanel: [],
        actionMenu: [],
        commandPanelMaxVisible: Infinity,
        commandPanelAreaWidth: null,
        commandPanelMenuWidth: null,
    }),
    setup() {
        const store = useMainStore();
        return { store }
    },
    created() {
        this.WIDGET = this.store.findWidget(this.widget_id).WIDGET
        this.content_Changed()
    },
    mounted() {
        this.$nextTick(() => {
            const container = this.$refs.commandPanelMenu
            const topLine = this.$refs.attr_top_line
            if (container && topLine && !this.commandPanelResizeObserver) {
                this.commandPanelResizeObserver = new ResizeObserver(() => this.updateCommandPanelOverflow())
                this.commandPanelResizeObserver.observe(topLine)
            }
            this.updateCommandPanelOverflow()
        })
    },
    beforeUnmount() {
        this.commandPanelResizeObserver?.disconnect()
        if (this._cpRafId) cancelAnimationFrame(this._cpRafId)
    },
    methods: {
        content_Changed(command) {
            if (this.initialAttr) {
                this.attr = this.initialAttr
                if (this.attr.dropdown) {
                    this.attr.dropdown.vueObj_content = this
                }
                const content = this.modelValue
                // IE 2026 09 04 / ODOO-336283: редактор в дропдауне — тип исходного значения помним один раз
                // (строка/пусто у строки формы → пишем строку; словарь у колонки грида → словарь как раньше)
                if (this._valueIsString === undefined) this._valueIsString = typeof content === 'string' || content == null || content === ''
                this.content_str = typeof content === 'string' ? content : this.store.CSS_stringify(content || {})
                this.content_str0 = this.content_str
                this.isError = false
                this.set_commandPanel()
                return
            }

            this.store.widget_attr_set(this)
            this.need_content_Changed_afterTimeout = true
            if (!this.isVisible) return
            if (this.content_Changed_timeout) { clearTimeout(this.content_Changed_timeout) }
            this.content_Changed_timeout = setTimeout(() => {
                this.content_Changed_afterTimeout(command)
            }, this.isVisible ? 1 : 700)
        },
        content_Changed_afterTimeout(command) {
            this.need_content_Changed_afterTimeout = false
            this.store.widget_attr_set(this)
            if (this.attr.cmOptions) {
                this.cmOptions = this.attr.cmOptions
            }
            this.cmOptions.hintOptions = {
                hint: this.cssHint,
                completeSingle: false,
            }

            let content = this.store.attr_get(this.WIDGET, this.attr) || {}
            // IE 2026 09 04 / ODOO-336283: поле хранит CSS СТРОКОЙ (Style строки лэйаута) — запоминаем тип
            // исходного значения один раз при загрузке; пустое значение у строки формы AttrCSS — тоже строка
            if (this._valueIsString === undefined) {
                const raw = this.store.attr_get(this.WIDGET, this.attr)
                this._valueIsString = typeof raw === 'string' || ((raw == null || raw === '') && this.attr?.attr_type === 'AttrCSS' && !!this.attr?.dropdown)
            }
            this.content_str = this.store.CSS_stringify(content)
            this.content_str0 = this.content_str
            this.isError = false
            this.set_commandPanel()
        },
        attr_set(input) {
            if (this._fromParent) return
            if (!this.editMode) {
                this._editSnapshot = this.content_str0
                this.editMode = true
                this.set_commandPanel()
            }
            this.isError = false
            if (this.content_str === this.content_str0) return
            try {
                let new_content = this.store.CSS_parse(this.content_str)
                // IE 2026 09 04 / ODOO-336283: строковое поле получает строку, а не словарь (иначе «[object Object]»)
                if (this._valueIsString) new_content = this.store.CSS_toLine(new_content)
                this.store.attr_set(this.WIDGET, this.attr, new_content)
            } catch (e) {
                this.isError = true
            }
        },
        editorReady(editor) {
            this.editor = editor

            const isWordChar = ch => /[\w.]/.test(ch);
            editor.on("inputRead", (cm, change) => {
                if (change.text && change.text.length === 1 && isWordChar(change.text[0])) {
                    CodeMirror.commands.autocomplete(cm, null, { completeSingle: false });
                }
            });
        },
        handleVisibility(isVisible) {
            if (this.isVisible === isVisible) return
            this.isVisible = isVisible
            if (isVisible && this.editor) {
                if (this.initialAttr) {
                    this.editor.refresh()
                } else {
                    this.content_Changed_afterTimeout()
                }
            }
        },
        cssHint(cm) {
            const CSS_PROPERTIES = [
                "color", "background-color", "font-size", "font-weight", "font-style",
                "text-decoration", "text-transform", "text-align", "line-height",
                "border",
                "border-radius", "border-color", "border-width", "border-style",
                "border-top",
                "border-top-radius", "border-top-color", "border-top-width", "border-top-style",
                "border-right",
                "border-right-radius", "border-right-color", "border-right-width", "border-right-style",
                "border-bottom",
                "border-bottom-radius", "border-bottom-color", "border-bottom-width", "border-bottom-style",
                "border-left",
                "border-left-radius", "border-left-color", "border-left-width", "border-left-style",
                "margin", "margin-top", "margin-right", "margin-bottom", "margin-left",
                "padding", "padding-top", "padding-right", "padding-bottom", "padding-left",
                "display", "position", "top", "right", "bottom", "left", "z-index",
                "width", "height", "min-width", "max-width", "min-height", "max-height",
                "overflow", "overflow-x", "overflow-y", "white-space", "word-wrap",
                "justify-content", "align-items", "align-content", "flex", "flex-direction",
                "grid-template-columns", "grid-template-rows", "gap", "cursor", "opacity",
                "box-shadow", "transition", "transform", "visibility"
            ];

            const CSS_VALUES = [
                "inherit", "initial", "unset", "none", "block", "inline", "inline-block",
                "flex", "grid", "absolute", "relative", "fixed", "sticky",
                "bold", "normal", "italic", "underline", "overline", "line-through",
                "left", "center", "right", "justify",
                "auto", "hidden", "visible", "scroll",
                "solid", "dashed", "dotted", "double", "groove",
                "transparent", "currentColor", "revert", "clip", "contain"
            ];

            const cur = cm.getCursor();
            const token = cm.getTokenAt(cur);
            const start = token.start;
            const end = cur.ch;
            const word = token.string;
            const from = { line: cur.line, ch: start };
            const to = { line: cur.line, ch: end };

            const isValue = token.state?.state === "propValue" || /[:;]/.test(cm.getLine(cur.line).substring(0, cur.ch));
            const list = (isValue ? CSS_VALUES : CSS_PROPERTIES)
                .filter(v => v.toLowerCase().startsWith(word.toLowerCase()) && v !== word)
                .sort((a, b) => a.localeCompare(b));

            return { list, from, to };
        },
        set_commandPanel() {
            const commandPanel = []

            // IE 2026 09 23 / ODOO-341407: i18n — подписи кнопок панели
            commandPanel.push({
                'attr_type': 'AttrButton', source_name: this.attr.name, 'binding': this.attr.binding,
                'active': this.editMode, 'title': this.t('Edit CSS'), 'command': 'onAttrJSON_toggleEdit__Click',
                'cssClass': 'pi p-button-white pi-pencil',
                isDropdown: !!(this.attr.dropdown),
                isCSS: true,
            })
            if (this.editMode) {
                commandPanel.push({
                    'attr_type': 'AttrButton', source_name: this.attr.name, 'binding': this.attr.binding,
                    'title': this.t('Apply'), 'command': 'onAttrJSON_apply__Click',
                    'cssClass': 'pi p-button-white pi-check',
                    isDropdown: !!(this.attr.dropdown),
                    isCSS: true,
                })
                commandPanel.push({
                    'attr_type': 'AttrButton', source_name: this.attr.name, 'binding': this.attr.binding,
                    'title': this.t('Cancel'), 'command': 'onAttrJSON_cancel__Click',
                    'cssClass': 'pi p-button-white pi-times',
                    isDropdown: !!(this.attr.dropdown),
                    isCSS: true,
                })
            }

            this.store.prepare_attrs(commandPanel, (this.attr.html_id || this.attr.id || 'css') + (++this.ind_commandPanel))
            this.commandPanel = commandPanel

            this.actionMenu = this.store.actionMenu_from_commandPanel(commandPanel)

            this._cpItemWidths = null
            this.commandPanelMaxVisible = Infinity
            this.commandPanelAreaWidth = null
            this.commandPanelMenuWidth = null
            this.$nextTick(() => {
                const container = this.$refs.commandPanelMenu
                const topLine = this.$refs.attr_top_line
                if (!container || !topLine) return
                if (!this.commandPanelResizeObserver) {
                    this.commandPanelResizeObserver = new ResizeObserver(() => this.updateCommandPanelOverflow())
                }
                this.commandPanelResizeObserver.observe(topLine)
                this.updateCommandPanelOverflow()
            })
        },
        updateCommandPanelOverflow() {
            if (this._cpRafId) cancelAnimationFrame(this._cpRafId)
            this._cpRafId = requestAnimationFrame(() => {
                this._cpRafId = null
                const cpArea = this.$refs.commandPanelArea
                const topLine = this.$refs.attr_top_line
                if (!cpArea || !topLine) return
                if (topLine.clientWidth <= 10) return
                let itemWidths = this._cpItemWidths
                if (!itemWidths?.length || itemWidths.every(w => w === 0)) {
                    const container = this.$refs.commandPanelMenu
                    itemWidths = container ? Array.from(container.children).map(el => el.offsetWidth) : []
                    if (!itemWidths.length || itemWidths.every(w => w === 0)) return
                    this._cpItemWidths = itemWidths
                }
                const available = cpArea.clientWidth
                let total = 0
                let maxVisible = itemWidths.length
                for (let i = 0; i < itemWidths.length; i++) {
                    total += itemWidths[i]
                    if (total > available) { maxVisible = i; break }
                }
                let overflowBtnWidth = 0
                if (maxVisible < itemWidths.length) {
                    const overflowBtnEl = cpArea.querySelector('.commandPanel-overflow-btn')
                    overflowBtnWidth = overflowBtnEl ? overflowBtnEl.offsetWidth : 28
                    const availableForItems = Math.max(available - overflowBtnWidth, 0)
                    total = 0
                    maxVisible = itemWidths.length
                    for (let i = 0; i < itemWidths.length; i++) {
                        total += itemWidths[i]
                        if (total > availableForItems) { maxVisible = i; break }
                    }
                }
                if (this.commandPanelMaxVisible !== maxVisible) this.commandPanelMaxVisible = maxVisible
                let visibleItemsWidth = itemWidths.slice(0, maxVisible).reduce((a, b) => a + b, 0)
                let visibleWidth = visibleItemsWidth + overflowBtnWidth
                if (!this.commandPanelOverflow.length) { visibleItemsWidth += 10; visibleWidth += 10 }
                if (this.commandPanelMenuWidth !== visibleItemsWidth) this.commandPanelMenuWidth = visibleItemsWidth
                if (this.commandPanelAreaWidth !== visibleWidth) this.commandPanelAreaWidth = visibleWidth
            })
        },
        menuNodeClicked(menu) {
            this.menuAction(menu.itemsSource[menu.selectedIndex])   // IE 2026 09 28 / ODOO-342342: выбор пункта wj-menu и MenuPanel (menuPanelPick) — одно действие
        },
        menuAction(action) {   // IE 2026 09 28 / ODOO-342342: прежнее тело menuNodeClicked
            const vueObj = this.WIDGET.vueObj
            this.store.executeStoreMethod(vueObj, action)
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(v) {
                if (!this.initialAttr) return
                const next = typeof v === 'string' ? v : this.store.CSS_stringify(v || {})
                if (next === this.content_str) return
                this._fromParent = true
                this.isError = false
                this.content_str = next
                this.$nextTick(() => { this._fromParent = false })
            },
        },
    },
    computed: {
        commandPanelOverflow() {
            if (this.commandPanelMaxVisible >= this.commandPanel.length) return []
            return this.commandPanel
                .slice(this.commandPanelMaxVisible)
                .filter(item => item.attr_type !== 'AttrSeparator' && item.visible !== false)
        },
    },
    components: {
      Codemirror,
    },
}
</script>

<style>
.cm-s-neo .CodeMirror-linenumber {
    color: #999;
}
.css-top-line {
    position: sticky;
    top: 0;
    z-index: 10;
    background: white;
    flex-shrink: 0;
    opacity: 1;
}
</style>

<style scoped>
.attr-css .codemirror-container {
    margin-top: 4px;
}
.css-error {
    position: absolute;
    left: 100px;
    top: 1px;

    z-index: 99;
    background-color: #fff4e5;
    border: 1px solid #f5c06a;
    color: #a65e00;
    padding: 10px 16px;
    margin-bottom: 12px;
    border-radius: 4px;
    font-weight: 500;
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    gap: 6px;
}
</style>
